import { useEffect, useRef } from 'react';
import { ChevronLeftIcon } from '@heroicons/react/24/outline';
import { SolidStatusPill } from '@odo/shared-ui';
import type { IteroOrder } from '../../../../../services/iteroBrowse';
import { BROWSE_FORMATTERS } from '../browseFormatters';
import { orderContext } from './orderContext';

interface OrderContextLineProps {
    order: IteroOrder;
    onBack?: () => void;
    // Set after navigating here inside the pane, so keyboard focus lands on the new view.
    focusOnMount: boolean;
}

export function OrderContextLine({ order, onBack, focusOnMount }: OrderContextLineProps) {
    const context = orderContext(order, BROWSE_FORMATTERS, onBack !== undefined);
    const backRef = useRef<HTMLButtonElement>(null);
    const headingRef = useRef<HTMLHeadingElement>(null);
    const shouldFocus = useRef(focusOnMount);

    useEffect(() => {
        if (shouldFocus.current) (backRef.current ?? headingRef.current)?.focus();
    }, []);

    return (
        <div className="flex h-10 shrink-0 items-center gap-2 border-t border-gray-100 bg-white pl-5 pr-4">
            {onBack && context.backLabel && (
                <>
                    <button
                        ref={backRef}
                        type="button"
                        onClick={onBack}
                        className="-ml-1 inline-flex shrink-0 items-center gap-0.5 rounded px-1 py-0.5 text-[13px] font-medium text-gray-500 transition-colors hover:text-gray-900 focus:outline-none focus-visible:ring-2 focus-visible:ring-gray-400"
                    >
                        <ChevronLeftIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        <span className="sr-only">Back to </span>
                        {context.backLabel}
                    </button>
                    <span aria-hidden="true" className="text-[13px] text-gray-300">
                        /
                    </span>
                </>
            )}
            <h4
                ref={headingRef}
                tabIndex={-1}
                title={context.title}
                className="min-w-0 truncate text-[13px] font-semibold text-gray-900 focus:outline-none"
            >
                {context.title}
            </h4>
            <span className="min-w-2 flex-1" />
            <span className="max-w-[160px] shrink-0">
                <SolidStatusPill value={order.status} />
            </span>
        </div>
    );
}
